'use client'

import { LabelWithRole } from '@skop-app/lib-domain/types'
import { uniqueBy } from 'remeda'

import { useUserContext } from '@/context'
import { useSelectedTeammates } from '@/hooks/useSelectedTeammates'

export const useReviewComposer = () => {
  const { organizationScope } = useUserContext()
  const { data: teammates = [] } = useSelectedTeammates(
    organizationScope.activeOrganizationId ?? undefined,
  )
  const teammatesList = uniqueBy(teammates, (teammate) => teammate.label)

  const getSuggestions = (message: string): LabelWithRole[] => {
    const parts = message.split(/\s+/)
    const token = parts[parts.length - 1] || ''
    const lastToken = token.startsWith('@') ? token.slice(1) : ''

    if (lastToken.length === 0) return teammatesList

    const query = lastToken.toLowerCase()
    return teammatesList.filter((teammate) =>
      teammate.label.toLowerCase().includes(query),
    )
  }

  const getLastToken = (message: string): string => {
    const parts = message.split(/\s+/)
    const token = parts[parts.length - 1] || ''
    return token.startsWith('@') ? token.slice(1) : ''
  }

  const escapeRegExp = (value: string): string =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  const extractMentions = (message: string): LabelWithRole[] => {
    if (!message || teammatesList.length === 0) return []

    const normalizedMessage = message.normalize('NFC')

    return teammatesList.filter((teammate) => {
      const label = (teammate.label || '').trim()
      if (!label) return false

      const escapedLabel = escapeRegExp(label)
      const pattern = new RegExp(
        '(?:^|[\\r\\n\\p{Zs}\\p{Zl}\\p{Zp}\\p{Ps}\\p{Pi}\\p{Pf}\\p{S}])@' +
          escapedLabel +
          '(?=$|[\\r\\n\\p{Zs}\\p{Zl}\\p{Zp}\\p{P}\\p{S}])',
        'iu',
      )

      return pattern.test(normalizedMessage)
    })
  }

  const replaceLastToken = (message: string, username: string): string => {
    const words = message.split(/\s+/)
    words[words.length - 1] = `@${username}`
    return words.join(' ') + ' '
  }

  return {
    teammatesList,
    getSuggestions,
    getLastToken,
    extractMentions,
    replaceLastToken,
  }
}
